import React, { useState, useEffect, useRef } from "react";
import { MdOutlineHeadsetMic } from "react-icons/md";

const TranscriptionConversation = ({
  conversationState,
  setConversationState,
  isLoading,
  onFinishLoading,
}) => {
  const {
    displayedMessages,
    currentMessage,
    currentChunkIndex,
    isFinished,
    chunks,
  } = conversationState;
  const chatEndRef = useRef(null);

  const speakerStyles = {
    default: { bgColor: "bg-gray-200", textColor: "text-gray-700" },
    0: { bgColor: "bg-blue-100", textColor: "text-blue-700" },
    1: { bgColor: "bg-green-100", textColor: "text-green-700" },
    2: { bgColor: "bg-purple-100", textColor: "text-purple-700" },
  };

  const formatTimestamp = (start, end) => {
    const formatTime = (time) => {
      const minutes = Math.floor(time / 60)
        .toString()
        .padStart(2, "0");
      const seconds = Math.floor(time % 60)
        .toString()
        .padStart(2, "0");
      return `${minutes}:${seconds}`;
    };
    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  useEffect(() => {
    if (isLoading && !isFinished && currentChunkIndex < chunks.length) {
      const interval = setInterval(() => {
        const currentChunk = chunks[currentChunkIndex];
        const nextMessage =
          currentMessage + currentChunk.text[currentMessage.length];

        setConversationState((prev) => ({
          ...prev,
          currentMessage: nextMessage,
        }));

        if (nextMessage.length === currentChunk.text.length) {
          clearInterval(interval);
          setConversationState((prev) => ({
            ...prev,
            displayedMessages: [
              ...prev.displayedMessages,
              { ...currentChunk, text: nextMessage },
            ],
            currentMessage: "",
            currentChunkIndex: prev.currentChunkIndex + 1,
          }));

          if (currentChunkIndex + 1 === chunks.length) {
            onFinishLoading();
            setConversationState((prev) => ({ ...prev, isFinished: true }));
          }
        }
      }, 5);

      return () => clearInterval(interval);
    }
  }, [isLoading, currentMessage, currentChunkIndex]);

  useEffect(() => {
    // Desplaza el scroll hacia abajo cuando se agregan nuevos mensajes
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [displayedMessages, currentMessage]);

  return (
    <div className="w-full max-w-3xl mx-auto">
      <div className="bg-white rounded-lg p-4 h-72 bg-gray-200 overflow-y-auto space-y-4 custom-scroll">
        {displayedMessages.map((message, index) => {
          const { bgColor, textColor } =
            speakerStyles[message.speaker] || speakerStyles.default;

          return (
            <div
              key={index}
              className={`flex items-center ${
                message.speaker % 2 === 0 ? "justify-start" : "justify-end"
              }`}
            >
              {message.speaker % 2 === 0 && (
                <div className="flex flex-col items-center justify-center mr-3 flex-shrink-0">
                  <MdOutlineHeadsetMic className="text-gray-500 text-xl" />
                  <span className="text-xs text-gray-500 w-16 text-center truncate whitespace-nowrap">
                    Speaker {message.speaker ?? "N/A"}
                  </span>
                </div>
              )}
              <div>
                <p
                  className={`text-xs text-gray-400 ${
                    message.speaker % 2 === 0 ? "text-left" : "text-right"
                  }`}
                >
                  {formatTimestamp(...message.timestamp)}
                </p>
                <div
                  className={`px-4 py-2 rounded-lg ${bgColor} ${textColor} text-sm shadow-sm`}
                >
                  {message.text}
                </div>
              </div>
              {message.speaker % 2 !== 0 && (
                <div className="flex flex-col items-center justify-center ml-3 flex-shrink-0">
                  <MdOutlineHeadsetMic className="text-gray-500 text-xl" />
                  <span className="text-xs text-gray-500 w-16 text-center truncate whitespace-nowrap">
                    Speaker {message.speaker ?? "N/A"}
                  </span>
                </div>
              )}
            </div>
          );
        })}

        {currentMessage && (
          <div
            className={`flex items-start ${
              chunks[currentChunkIndex]?.speaker % 2 === 0
                ? "justify-start"
                : "justify-end"
            }`}
          >
            {chunks[currentChunkIndex]?.speaker % 2 === 0 && (
              <div className="flex flex-col items-center justify-center mr-3 flex-shrink-0">
                <MdOutlineHeadsetMic className="text-gray-500 text-xl" />
                <span className="text-xs text-gray-500 mt-1 w-16 text-center truncate whitespace-nowrap">
                  Speaker {chunks[currentChunkIndex]?.speaker ?? "N/A"}
                </span>
              </div>
            )}
            <div>
              <p
                className={`text-xs text-gray-400 ${
                  chunks[currentChunkIndex]?.speaker % 2 === 0
                    ? "text-left"
                    : "text-right"
                }`}
              >
                {formatTimestamp(
                  ...(chunks[currentChunkIndex]?.timestamp ?? [0, 0])
                )}
              </p>
              <div
                className={`px-4 py-2 rounded-lg ${
                  speakerStyles[chunks[currentChunkIndex]?.speaker]?.bgColor ||
                  speakerStyles.default.bgColor
                } ${
                  speakerStyles[chunks[currentChunkIndex]?.speaker]
                    ?.textColor || speakerStyles.default.textColor
                } text-sm shadow-sm`}
              >
                {currentMessage}
              </div>
            </div>
            {chunks[currentChunkIndex]?.speaker % 2 !== 0 && (
              <div className="flex flex-col items-center justify-center ml-3 flex-shrink-0">
                <MdOutlineHeadsetMic className="text-gray-500 text-xl" />
                <span className="text-xs text-gray-500 mt-1 w-16 text-center truncate whitespace-nowrap">
                  Speaker {chunks[currentChunkIndex]?.speaker ?? "N/A"}
                </span>
              </div>
            )}
          </div>
        )}
        {/* Referencia para desplazar el scroll */}
        <div ref={chatEndRef} />
      </div>
    </div>
  );
};

export default TranscriptionConversation;
